import { useState, type ReactNode } from 'react';
import {
  Scanner,
  type IDetectedBarcode,
  type IScannerError,
  type ScannerErrorKind,
} from '@yudiel/react-qr-scanner';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Field } from '@/components/synapse/Field';
import { Icon } from '@/components/synapse/Icon';

/**
 * Bottom-sheet/modal shell shared by the Add Stock and Issue scanners. The
 * dialog drives the camera (or a text fallback) and forwards the decoded code
 * to the caller via `onCodeChange`; the caller injects its own lookup hook and
 * preview UI via `renderPreview`, and decides when the "Use" button is enabled.
 */
export interface ScanDialogProps {
  title?: string;
  description?: string;
  /** Called with the trimmed code when the user confirms. */
  onUse: (code: string) => void;
  onClose: () => void;
  /** Notified on every input change with the raw (untrimmed) string. Lets the
   *  caller drive its own lookup hook without owning the input element. */
  onCodeChange?: (code: string) => void;
  /** Should the "Use" button be active for this code? Defaults to non-empty. */
  canUse?: (code: string) => boolean;
  /** Custom label for the confirm button. */
  useLabel?: string;
  /** Render the preview pane shown beneath the input once `code` is non-trivial. */
  renderPreview?: (code: string) => ReactNode;
  /** Minimum trimmed length before `renderPreview` is shown. Default 3. */
  previewMinLength?: number;
}

type Mode = 'camera' | 'text';

// Camera will never work in this session — flip straight to the text fallback.
const FATAL_ERROR_KEYS: Partial<Record<ScannerErrorKind, string>> = {
  'permission-denied': 'scan.err.permissionDenied',
  'no-camera': 'scan.err.noCamera',
  'insecure-context': 'scan.err.insecureContext',
  unsupported: 'scan.err.unsupported',
  security: 'scan.err.security',
};

// Recoverable — surface a banner but keep the camera mode active so the user
// can retry (close another tab, plug a webcam back in, etc.).
const RECOVERABLE_ERROR_KEYS: Partial<Record<ScannerErrorKind, string>> = {
  'in-use': 'scan.err.inUse',
  overconstrained: 'scan.err.overconstrained',
};

export function ScanDialog({
  title,
  description,
  onUse,
  onClose,
  onCodeChange,
  canUse,
  useLabel,
  renderPreview,
  previewMinLength = 3,
}: ScanDialogProps) {
  const { t } = useTranslation();
  const resolvedTitle = title ?? t('scan.title');
  const resolvedDescription = description ?? t('scan.description');
  const resolvedUseLabel = useLabel ?? t('scan.use');
  const [mode, setMode] = useState<Mode>('camera');
  const [code, setCodeState] = useState('');
  const [paused, setPaused] = useState(false);
  const [fatalNote, setFatalNote] = useState<string | null>(null);
  const [recoverableNote, setRecoverableNote] = useState<string | null>(null);
  const trimmed = code.trim();
  const enabled = canUse ? canUse(trimmed) : trimmed.length > 0;

  const updateCode = (next: string) => {
    setCodeState(next);
    onCodeChange?.(next);
  };

  const onDetected = (detected: IDetectedBarcode[]) => {
    const raw = detected[0]?.rawValue?.trim();
    if (!raw) return;
    updateCode(raw);
    setPaused(true);
  };

  const onScannerError = (err: IScannerError) => {
    const fatalKey = FATAL_ERROR_KEYS[err.kind];
    if (fatalKey) {
      setFatalNote(t(fatalKey));
      setMode('text');
      setPaused(false);
      return;
    }
    const recoverableKey = RECOVERABLE_ERROR_KEYS[err.kind];
    if (recoverableKey) {
      setRecoverableNote(t(recoverableKey));
      return;
    }
    // Unknown / aborted / type-error: log so the failure isn't invisible to
    // anyone watching the console while debugging. No user-facing surface
    // because there's nothing actionable.
    console.warn('[ScanDialog] scanner error', err);
  };

  const rescan = () => {
    updateCode('');
    setPaused(false);
  };

  const switchToText = () => {
    setMode('text');
    setPaused(false);
    updateCode('');
  };

  const switchToCamera = () => {
    setMode('camera');
    setPaused(false);
    updateCode('');
    setRecoverableNote(null);
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={resolvedTitle}
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 p-0 sm:items-center sm:p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="w-full max-w-md rounded-t-2xl bg-paper p-5 sm:rounded-2xl">
        <div className="flex items-center justify-between">
          <div className="text-[15px] font-semibold tracking-[-0.01em]">{resolvedTitle}</div>
          <button
            type="button"
            onClick={onClose}
            aria-label={t('common.close')}
            className="flex h-8 w-8 items-center justify-center rounded-md text-muted hover:bg-paper-2 hover:text-ink"
          >
            <Icon name="x" size={18} />
          </button>
        </div>
        <p className="mt-1 text-[12.5px] text-muted">
          {mode === 'camera'
            ? resolvedDescription
            : fatalNote ?? t('scan.typePrompt')}
        </p>

        {mode === 'camera' ? (
          <div className="mt-3.5 overflow-hidden rounded-lg border border-line bg-black">
            <div className="relative aspect-[4/3] w-full">
              <Scanner
                onScan={onDetected}
                onError={onScannerError}
                paused={paused}
                constraints={{ facingMode: 'environment' }}
                allowMultiple={false}
                sound={false}
                styles={{
                  container: { width: '100%', height: '100%' },
                  video: { width: '100%', height: '100%', objectFit: 'cover' },
                }}
              />
              {paused && (
                <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                  <span className="rounded-md bg-paper px-3 py-1 text-[12px] font-medium text-ink">
                    {t('scan.captured')}
                  </span>
                </div>
              )}
            </div>
            {recoverableNote && (
              <div className="border-t border-line bg-destructive-bg/40 px-3 py-2 text-[12px] text-destructive">
                {recoverableNote}
              </div>
            )}
            <div className="flex items-center justify-between gap-2 border-t border-line bg-paper px-3 py-2">
              <button
                type="button"
                className="text-[12.5px] text-muted hover:text-ink"
                onClick={switchToText}
              >
                {t('scan.typeInstead')}
              </button>
              {paused && (
                <button
                  type="button"
                  className="text-[12.5px] font-medium text-accent hover:opacity-80"
                  onClick={rescan}
                >
                  {t('scan.rescan')}
                </button>
              )}
            </div>
          </div>
        ) : (
          <div className="mt-3.5">
            <Field label={t('common.barcode')}>
              <Input
                autoFocus
                mono
                value={code}
                onChange={(e) => updateCode(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && enabled) {
                    e.preventDefault();
                    onUse(trimmed);
                  }
                }}
                placeholder={t('scan.barcodePlaceholder')}
              />
            </Field>
            {!fatalNote && (
              <button
                type="button"
                className="mt-2 text-[12.5px] text-muted hover:text-ink"
                onClick={switchToCamera}
              >
                {t('scan.useCameraInstead')}
              </button>
            )}
          </div>
        )}

        {trimmed.length >= previewMinLength && renderPreview && (
          <div className="mt-3 rounded-md border border-line bg-paper-2 px-3 py-2 text-[12.5px]">
            {renderPreview(trimmed)}
          </div>
        )}
        <div className="mt-4 flex gap-2">
          <Button variant="secondary" full onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button full disabled={!enabled} onClick={() => onUse(trimmed)}>
            {resolvedUseLabel}
          </Button>
        </div>
      </div>
    </div>
  );
}
